import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { toast } from "sonner";
import { History, Loader2, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/App";

export const ACTIONS = {
  "reservation.create": { label: "Rezervasyon oluşturuldu", cls: "bg-emerald-100 text-emerald-700" },
  "reservation.update": { label: "Rezervasyon değiştirildi", cls: "bg-sky-100 text-sky-700" },
  "reservation.confirm": { label: "Onaylandı", cls: "bg-sky-100 text-sky-700" },
  "reservation.approve": { label: "Onaylandı", cls: "bg-sky-100 text-sky-700" },
  "reservation.reject": { label: "Reddedildi", cls: "bg-orange-100 text-orange-700" },
  "reservation.complete": { label: "Tamamlandı", cls: "bg-green-100 text-green-700" },
  "reservation.cancel": { label: "İptal edildi", cls: "bg-amber-100 text-amber-700" },
  "reservation.burn": { label: "Ders yakıldı", cls: "bg-rose-100 text-rose-700" },
  "reservation.restore": { label: "Geri alındı", cls: "bg-indigo-100 text-indigo-700" },
  "reservation.commission": { label: "Komisyon değişti", cls: "bg-violet-100 text-violet-700" },
  "reservation.delete": { label: "Rezervasyon silindi", cls: "bg-red-100 text-red-700" },
  "member.delete": { label: "Üye silindi", cls: "bg-red-100 text-red-700" },
  "member.restrict": { label: "Online kısıtlama verildi", cls: "bg-red-100 text-red-700" },
  "member.unrestrict": { label: "Online kısıtlama kaldırıldı", cls: "bg-emerald-100 text-emerald-700" },
  "member_package.delete": { label: "Üye paketi silindi", cls: "bg-red-100 text-red-700" },
  "package.delete": { label: "Paket şablonu silindi", cls: "bg-red-100 text-red-700" },
};
const FILTERS = [
  ["all", "Tüm işlemler"], ["reservation.create", "Oluşturma"], ["reservation.update", "Değişiklik"],
  ["reservation.cancel", "İptal"], ["reservation.complete", "Tamamlama"], ["reservation.burn", "Yakma"],
  ["reservation.restore", "Geri alma"], ["reservation.delete", "Rezervasyon silme"], ["member.delete", "Üye silme"], ["member.restrict", "Online kısıtlama"],
  ["member_package.delete", "Üye paketi silme"], ["package.delete", "Paket silme"],
];
const STATUS = { approved: "Onaylandı", confirmed: "Onaylandı", completed: "Tamamlandı", cancelled: "İptal", burned: "Yakıldı", pending: "Beklemede", rejected: "Reddedildi", deleted: "Silindi" };
const FIELD = { date: "Tarih", start_time: "Saat", end_time: "Bitiş", trainer_name: "Eğitmen", room_id: "Salon", status: "Durum", member_package_id: "Paket", frozen_commission: "Komisyon" };
const ROLE = { business: "İşletme sahibi", trainer: "Eğitmen", employee: "Çalışan", member: "Üye", public: "Link" };
const fmt = (iso) => new Date(iso).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const AuditRow = ({ it }) => {
  const a = ACTIONS[it.action] || { label: it.action, cls: "bg-slate-100 text-slate-600" };
  const changes = Object.entries(it.changes || {}).filter(([k]) => k !== "status");
  return (
    <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4 py-3 border-b last:border-0" data-testid={`audit-row-${it.audit_id}`}>
      <div className="text-xs text-grey md:w-32 shrink-0">{fmt(it.created_at)}</div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`${a.cls} border-0`}>{a.label}</Badge>
          <span className="text-sm font-medium text-navy truncate">{it.entity_label || "-"}</span>
        </div>
        {it.before_status && it.after_status && it.before_status !== it.after_status && (
          <p className="text-xs text-grey">Durum: {STATUS[it.before_status] || it.before_status} → {STATUS[it.after_status] || it.after_status}</p>
        )}
        {changes.map(([k, v]) => (
          <p key={k} className="text-xs text-grey">{FIELD[k] || k}: {String(v.from ?? "-")} → {String(v.to ?? "-")}</p>
        ))}
        {it.reason && <p className="text-xs text-slate-700">Sebep: <span className="italic">{it.reason}</span></p>}
      </div>
      <div className="text-xs md:text-right md:w-40 shrink-0">
        <p className="font-medium text-navy">{it.actor_name}</p>
        <p className="text-grey">{ROLE[it.actor_role] || it.actor_role}</p>
      </div>
    </div>
  );
};

export default function AuditLogModule({ businessId }) {
  const [data, setData] = useState({ items: [], total: 0, actors: [] });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ start_date: "", end_date: "", actor_id: "all", action: "all", q: "" });
  const [page, setPage] = useState(1);
  const limit = 50;

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    const p = new URLSearchParams({ page, limit });
    Object.entries(f).forEach(([k, v]) => v && v !== "all" && p.set(k, v));
    try {
      const r = await fetch(`${API}/businesses/${businessId}/audit-logs?${p}`, { credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail);
      setData(j);
    } catch (e) {
      toast.error(e.message || "Denetim kaydı yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [businessId, f, page]);

  useEffect(() => { load(); }, [load]);
  const set = (k, v) => { setPage(1); setF((o) => ({ ...o, [k]: v })); };
  const pages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div className="space-y-6" data-testid="audit-log-module">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy flex items-center gap-2"><History className="w-5 h-5" /> Denetim Kaydı</h2>
          <p className="text-grey text-sm">Kim, ne zaman, hangi işlemi ve neden yaptı</p>
        </div>
        <Button variant="outline" className="rounded-xl" onClick={load} data-testid="audit-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Yenile
        </Button>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <DatePickerTR value={f.start_date} onChange={(v) => set("start_date", v || "")} returnDateString placeholder="Başlangıç" data-testid="audit-start-date" />
          <DatePickerTR value={f.end_date} onChange={(v) => set("end_date", v || "")} returnDateString placeholder="Bitiş" data-testid="audit-end-date" />
          <Select value={f.actor_id} onValueChange={(v) => set("actor_id", v)}>
            <SelectTrigger className="rounded-xl" data-testid="audit-actor-filter"><SelectValue placeholder="Kullanıcı" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm kullanıcılar</SelectItem>
              {data.actors.map((a) => <SelectItem key={a.actor_id} value={a.actor_id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.action} onValueChange={(v) => set("action", v)}>
            <SelectTrigger className="rounded-xl" data-testid="audit-action-filter"><SelectValue placeholder="İşlem" /></SelectTrigger>
            <SelectContent>{FILTERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Üye / ders ara" className="rounded-xl" data-testid="audit-search-input" />
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
          ) : data.items.length === 0 ? (
            <p className="text-center text-grey py-12" data-testid="audit-empty">Bu filtrelerle kayıt bulunamadı</p>
          ) : (
            data.items.map((it) => <AuditRow key={it.audit_id} it={it} />)
          )}
        </CardContent>
      </Card>
      <div className="flex items-center justify-between text-sm text-grey">
        <span data-testid="audit-total">{data.total} kayıt</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
          <span>{page} / {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="audit-next-page"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}
